import { Routes, Route, useSearchParams } from "react-router-dom";
import Main from "./pages/main";
import Login from "./pages/login";
import Registration from "./pages/registration";
import Verify from "./pages/verify";
import TestYourself from './pages/test-yourself';
import Header from "./components/Header";
import Categories from "./pages/categories";
import Payment from "./pages/payment";
import Modal from "./modals";
import Footer from "./components/Footer";
import UsageConsiderations from "./pages/usage-considerations";
import { getUserInfo } from "./api/auth";
import { useDispatch, useSelector } from "react-redux";
import { setUser } from "./redux/me.slice";
import { useQuery } from "@tanstack/react-query";
import { Navigate } from "react-router-dom";
import { useCategoriesQuery } from "./api/mutations/categories.query";
import Loader from "./components/Loader";
import { useLayoutEffect } from "react";
import { useLocation } from "react-router-dom";

export const useUserInfoQuery = () => {
  const dispatch = useDispatch();

  const { isLoading } = useQuery({
    queryKey: ["user-info"],
    queryFn: async () => {
      try {
        const token = localStorage.getItem("assessmentai-token");
        if (!token) {
          throw { token: false };
        }

        const response = await getUserInfo();
        const userData = response.data;
        dispatch(setUser(userData));

        return true;
      } catch (error) {
        return false;
      }
    },
    retry: false,
    refetchOnWindowFocus: false,
  });

  return { isLoading };
};

const ProtectedRoute = ({ user, children }) => {
  const params = new URLSearchParams(window.location.search);
  const pathName = window.location.pathname;
  const testYourselfId = params.get("test-yourself");

  if (!user) {
    if (testYourselfId) {
      return <Navigate to={`/login?test-yourself=${testYourselfId}`} replace />;
    } else if (pathName === "/payment") {
      return <Navigate to="/login?payment=true" replace />;
    } else {
      return <Navigate to="/login" replace />;
    }
  }
  return children;
};

const PublicRoute = ({ user, children }) => {
  const params = new URLSearchParams(window.location.search);
  const pathName = window.location.pathname;
  const testYourselfId = params.get("test-yourself");
  const isPayment = params.get("payment");

  if (user) {
    if (testYourselfId) {
      return <Navigate to={`/?test-yourself=${testYourselfId}`} replace />;
    } else if (pathName === "/verify" || isPayment) {
      return <Navigate to="/payment" replace />;
    } else {
      return <Navigate to="/" replace />;
    }
  }
  return children;
};

function App() {
  const location = useLocation();
  const modalIds = useSelector((state) => state.modal.modalIds);
  const modalData = useSelector((state) => state.modal.modalData);
  const currentModalIndex = useSelector(
    (state) => state.modal.currentModalIndex
  );
  const user = useSelector((state) => state.me.data);
  const { isLoading } = useUserInfoQuery();
  const [searchParams] = useSearchParams();

  useLayoutEffect(() => {
    const token = searchParams.get("token");
    if (token) {
      localStorage.setItem("assessmentai-token", token);
    }
  }, []);
  useCategoriesQuery();

  if (isLoading) return <Loader />;

  return (
    <>
      {location.pathname !== '/test/' &&
        <Header />
      }
      <Routes>
        <Route
          path="/"
          element={
            <ProtectedRoute user={user}>
              <Main />
            </ProtectedRoute>
          }
        />
        <Route
          path="/categories/:slug"
          element={
            <ProtectedRoute user={user}>
              <Categories />
            </ProtectedRoute>
          }
        />
        <Route
          path="/payment"
          element={
            <ProtectedRoute user={user}>
              <Payment />
            </ProtectedRoute>
          }
        />
        <Route
          path="/login"
          element={
            <PublicRoute user={user}>
              <Login />
            </PublicRoute>
          }
        />
        <Route
          path="/registration"
          element={
            <PublicRoute user={user}>
              <Registration />
            </PublicRoute>
          }
        />
        <Route
          path="/verify"
          element={
            <PublicRoute user={user}>
              <Verify />
            </PublicRoute>
          }
        />
        <Route
          path="/test"
          element={
            <ProtectedRoute user={user}>
              <TestYourself />
            </ProtectedRoute>
          }
        />
        <Route
          path="/usage-considerations"
          element={<UsageConsiderations />}
        />
      </Routes>

      {location.pathname !== '/test/' &&
        <Footer />
      }

      {modalIds?.length > 0 && modalIds[currentModalIndex] && (
        <Modal
          modalId={modalIds[currentModalIndex]}
          modalData={modalData[currentModalIndex]}
        />
      )}
    </>
  );
}

export default App;
